const GOOGLE_DRIVE_HOSTS = ['drive.google.com'];
const GOOGLE_WORKSPACE_PATH_PREFIXES = ['/presentation', '/document', '/spreadsheets'];
const CLOUDFRONT_HOST_SUFFIX = '.cloudfront.net';

const getUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    return new URL(url);
  } catch (error) {
    return null;
  }
};

export const isGoogleDriveUrl = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl) {
    return false;
  }

  return GOOGLE_DRIVE_HOSTS.some((host) => parsedUrl.hostname.endsWith(host));
};

export const isGoogleWorkspaceUrl = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl) {
    return false;
  }

  return parsedUrl.hostname === 'docs.google.com' && GOOGLE_WORKSPACE_PATH_PREFIXES.some(
    (path) => parsedUrl.pathname === path || parsedUrl.pathname.startsWith(`${path}/`),
  );
};

const getGoogleWorkspaceFile = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl || !isGoogleWorkspaceUrl(url)) {
    return null;
  }

  const [, type, dSegment, fileId] = parsedUrl.pathname.split('/');

  if (dSegment !== 'd' || !fileId || fileId === 'e') {
    return null;
  }

  return { type, fileId };
};

const getGoogleWorkspaceDownloadUrl = (url) => {
  const workspaceFile = getGoogleWorkspaceFile(url);

  if (!workspaceFile) {
    return url;
  }

  const { type, fileId } = workspaceFile;

  switch (type) {
    case 'presentation':
      return `https://docs.google.com/presentation/d/${fileId}/export/pptx`;
    case 'document':
      return `https://docs.google.com/document/d/${fileId}/export?format=docx`;
    case 'spreadsheets':
      return `https://docs.google.com/spreadsheets/d/${fileId}/export?format=xlsx`;
    default:
      return url;
  }
};

export const isCloudFrontUrl = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl) {
    return false;
  }

  return parsedUrl.hostname === 'cloudfront.net' || parsedUrl.hostname.endsWith(CLOUDFRONT_HOST_SUFFIX);
};

const isS3Url = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl) {
    return false;
  }

  return parsedUrl.hostname.includes('.s3.') || parsedUrl.hostname.includes('s3.amazonaws.com');
};

export const getGoogleDriveFileId = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl || isGoogleWorkspaceUrl(url) || !isGoogleDriveUrl(url)) {
    return null;
  }

  const idFromQuery = parsedUrl.searchParams.get('id');

  if (idFromQuery) {
    return idFromQuery;
  }

  const filePathMatch = parsedUrl.pathname.match(/\/file\/d\/([^/]+)/);

  if (filePathMatch) {
    return filePathMatch[1];
  }

  const openPathMatch = parsedUrl.pathname.match(/\/open\/([^/]+)/);

  return openPathMatch ? openPathMatch[1] : null;
};

export const isGoogleDriveThumbnailUrl = (url) => {
  const parsedUrl = getUrl(url);

  return Boolean(parsedUrl && isGoogleDriveUrl(url) && parsedUrl.pathname.includes('/thumbnail'));
};

export const getGoogleDriveThumbnailUrl = (url) => {
  const fileId = getGoogleDriveFileId(url);

  return fileId ? `https://lh3.googleusercontent.com/d/${fileId}=w1000` : url;
};

export const getGoogleDrivePreviewUrl = (url) => {
  const fileId = getGoogleDriveFileId(url);

  return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url;
};

export const getGoogleDriveDownloadUrl = (url) => {
  const fileId = getGoogleDriveFileId(url);

  return fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : url;
};

export const getMediaProvider = (url) => {
  const parsedUrl = getUrl(url);

  if (!parsedUrl) {
    return 'unknown';
  }

  if (isGoogleDriveUrl(url)) {
    return 'google-drive';
  }

  if (isCloudFrontUrl(url)) {
    return 'cloudfront';
  }

  if (isS3Url(url)) {
    return 's3';
  }

  return 'direct';
};

const CONTENT_STORE_CARD_OVERLAY_GRADIENT = 'linear-gradient(to bottom, rgba(5, 33, 64, 0), rgba(0, 0, 0, 0.5))';

export const getContentStoreCardBackgroundStyle = (thumbnail) => {
  const trimmedThumbnail = typeof thumbnail === 'string' ? thumbnail.trim() : '';

  if (!trimmedThumbnail) {
    return undefined;
  }

  const backgroundUrl = normalizeMediaUrl(trimmedThumbnail, 'background');

  if (!backgroundUrl) {
    return undefined;
  }

  return {
    backgroundImage: `${CONTENT_STORE_CARD_OVERLAY_GRADIENT}, url(${backgroundUrl})`,
  };
};

export const normalizeMediaUrl = (url, usage = 'direct') => {
  if (isGoogleWorkspaceUrl(url)) {
    if (usage === 'download') {
      return getGoogleWorkspaceDownloadUrl(url);
    }

    return url;
  }

  if (!isGoogleDriveUrl(url)) {
    return url;
  }

  switch (usage) {
    case 'image':
    case 'thumbnail':
    case 'background':
      return getGoogleDriveThumbnailUrl(url);
    case 'preview':
    case 'iframe':
    case 'video-preview':
      return getGoogleDrivePreviewUrl(url);
    case 'download':
      return getGoogleDriveDownloadUrl(url);
    default:
      return url;
  }
};
